import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Card from "../components/Card";
import { ClientSection, ReachOutSection } from "../components/BottomSection";
import SEO from "../components/SEO";
import axios from "../lib/axios";
import BrandEndorsementQuotationModal from "../components/BrandEndorsementQuotationModal";
import OutdoorItemsBrowser from "../components/OutdoorItemsBrowser";
import { formatTierName, formatMediaType, formatAudience, formatFieldValue } from "../utils/formatters";
import { FaPlay } from "react-icons/fa";

const CategoryDetails = ({ categoryName }) => {
  const params = useParams();
  const category = categoryName || params.category;
  const slug = params.slug;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mainAd, setMainAd] = useState(null);
  const [childAds, setChildAds] = useState([]);
  const [error, setError] = useState(null);
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [showQuotationModal, setShowQuotationModal] = useState(false);

  // Extract YouTube video ID from URL
  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  useEffect(() => {
    // Outdoor categories list their inventory items server-side
    if ((category || "").toLowerCase().trim().replace(/-advertising$/, "").replace(/-ads$/, "") === "outdoor") {
      return;
    }
    const fetchCategoryDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const normalizedCategory = (category || "")
          .toLowerCase()
          .trim()
          .replace(/-advertising$/, "")
          .replace(/-ads$/, "");

        let url;
        switch (normalizedCategory) {
          case "radio":
            url = `/api/radio-ads?where[and][0][slug][equals]=${slug}&where[and][1][enabled][not_equals]=false`;
            break;
          case "cinema":
            url = `/api/cinema-ads?where[and][0][slug][equals]=${slug}&where[and][1][enabled][not_equals]=false`;
            break;
          case "transit":
            url = `/api/transit-ads?where[and][0][slug][equals]=${slug}&where[and][1][enabled][not_equals]=false`;
            break;
          case "outdoor":
            url = `/api/outdoor-ads?where[and][0][slug][equals]=${slug}&where[and][1][enabled][not_equals]=false`;
            break;
          case "non-traditional":
            url = `/api/non-traditional-ads?where[and][0][slug][equals]=${slug}&where[and][1][enabled][not_equals]=false`;
            break;
          case "btl":
            url = `/api/btl-ads?where[and][0][slug][equals]=${slug}&where[and][1][enabled][not_equals]=false`;
            break;
          case "digital":
            url = `/api/digital-ads?where[and][0][slug][equals]=${slug}&where[and][1][enabled][not_equals]=false`;
            break;
          case "newspaper":
            url = `/api/newspaper-ads?where[and][0][slug][equals]=${slug}&where[and][1][enabled][not_equals]=false`;
            break;
          default:
            url = `/api/${normalizedCategory}-ads?where[and][0][slug][equals]=${slug}&where[and][1][enabled][not_equals]=false`;
        }

        const response = await axios.get(url);
        if (response.status === 200) {
          const baseAd = response.data.docs?.[0];
          if (!baseAd || baseAd.enabled === false) {
            setMainAd(null);
            setChildAds([]);
            setError("No details found for this category");
            return;
          }

          setMainAd(baseAd);
          const options = baseAd.options || [];
          const optionsWithDetails = options.map((option) => {
            const { options: _, image, youtube_video_card, youtube_video_banner, ...mainAdRest } = baseAd;
            return {
              ...mainAdRest,
              ...option,
              youtube_video: option.youtube_video || null,
            };
          });
          setChildAds(optionsWithDetails);
        } else {
          setError(response.data.message || "Failed to fetch details");
        }
      } catch (err) {
        setError(
          err.response?.data?.message ||
            "An error occurred while fetching details"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryDetails();
  }, [category, slug]);

  const normalizedCategoryEarly = (category || "")
    .toLowerCase()
    .trim()
    .replace(/-advertising$/, "")
    .replace(/-ads$/, "");

  // Outdoor: category hero + server-searchable inventory items
  if (normalizedCategoryEarly === "outdoor") {
    return (
      <div className="min-h-screen bg-gray-50">
        <SEO
          title="Outdoor Advertising"
          description="Find the best outdoor advertising options at Indorinama. Billboards, hoardings, unipoles and more across India."
          keywords="outdoor advertising, billboard advertising, hoarding, unipole, outdoor ads, Indorinama"
          url={`https://indorinama.com/outdoor-advertising${slug ? `/${slug}` : ""}`}
        />
        <Navbar />
        <OutdoorItemsBrowser slug={slug} />
        <ClientSection />
        <ReachOutSection />
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex justify-center items-center h-[60vh]">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex justify-center items-center h-[60vh]">
          <div className="text-red-500 text-xl">{error}</div>
        </div>
      </div>
    );
  }

  const mainAdOptionsAllOnRequest =
    !!mainAd?.options?.length &&
    mainAd.options.every((option) => option?.price_on_request);

  return (
    <>
      <SEO
        title={`${category?.charAt(0).toUpperCase() + category?.slice(1)} Advertising`}
        description={`Find the best ${category} advertising options at Indorinama. Get professional advertising services across ${category} platforms.`}
        keywords={`${category} advertising, ${category} ads, advertising services, Indorinama`}
        url={`https://indorinama.com/${category}-advertising${slug ? `/${slug}` : ''}`}
      />
      <div className="min-h-screen">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>

        {mainAd && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="relative">
                <img
                  src={
                    mainAd?.detailImage?.url
                      ? mainAd.detailImage.url
                      : "/placeholder.jpg"
                  }
                  alt={mainAd.title}
                  className="w-full h-[200px] object-cover rounded-lg"
                />
                {/* YouTube Play Button for Radio and Transit Ads */}
                {(category === "radio" || category === "transit") && mainAd.youtube_video_banner && (
                  <div
                    className="absolute bottom-2 right-2 cursor-pointer"
                    onClick={() => setShowYoutubeModal(true)}
                  >
                    <div className="bg-white rounded-full p-2 hover:bg-gray-100 transition-colors shadow-lg">
                      <FaPlay className="text-red-600 text-2xl" />
                    </div>
                  </div>
                )}
              </div>
              <div>
                <h1 className="text-3xl font-bold mb-4">{mainAd.title}</h1>
                <p className="text-gray-600 mb-6">{mainAd.description}</p>
                {((category === "outdoor" &&
                  !((mainAd?.minimum_billing_amount && Number(mainAd.minimum_billing_amount) > 0) || (mainAd?.offer_rate && Number(mainAd.offer_rate) > 0) || (mainAd?.price && Number(mainAd.price) > 0))) ||
                  mainAdOptionsAllOnRequest) && (
                  <button
                    onClick={() => setShowQuotationModal(true)}
                    className="inline-flex items-center px-6 py-2.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-sm mb-4"
                  >
                    Get Quotation
                  </button>
                )}
              </div>
            </div>
            <div className="space-x-4 flex flex-wrap gap-2 md:items-center w-full mt-4">
              <div className="flex md:items-center bg-gray-100 rounded-lg p-2">
                <span className="font-semibold">Location:</span>
                <span>
                  {mainAd.locations
                    ?.filter(loc => loc != null)
                    .map((loc) => loc.city || loc.state || loc.country)
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
              {mainAd.lanugage && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Language:</span>
                  <span>{mainAd.lanugage}</span>
                </div>
              )}
              {mainAd.languages && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Languages:</span>
                  <span>{mainAd.languages?.join(", ")}</span>
                </div>
              )}
              {mainAd.specifications && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Specifications:</span>
                  <span>{mainAd.specifications}</span>
                </div>
              )}
              {mainAd.station && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Station:</span>
                  <span>{mainAd.station}</span>
                </div>
              )}
              {mainAd.frequency && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Frequency:</span>
                  <span>{mainAd.frequency}</span>
                </div>
              )}
              {mainAd.audience && mainAd.audience.length > 0 && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Audience:</span>
                  <span>{formatAudience(mainAd.audience)}</span>
                </div>
              )}
              {mainAd.tier && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Tier:</span>
                  <span>{formatTierName(mainAd.tier)}</span>
                </div>
              )}
              {mainAd.popularity && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Rank:</span>
                  <span>{mainAd.popularity}</span>
                </div>
              )}
              {mainAd.cinemaChain && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Cinema Chain:</span>
                  <span>{mainAd.cinemaChain}</span>
                </div>
              )}
              {mainAd.screen && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Screen:</span>
                  <span>{mainAd.screen}</span>
                </div>
              )}
              {mainAd.screenRecommended && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Screen Recommended:</span>
                  <span>{mainAd.screenRecommended}</span>
                </div>
              )}
              {mainAd.seats && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Seats:</span>
                  <span>{mainAd.seats}</span>
                </div>
              )}
              {mainAd.totalScreens && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Total Screens:</span>
                  <span>{mainAd.totalScreens}</span>
                </div>
              )}
              {mainAd.mediaType && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Media Type:</span>
                  <span>{formatMediaType(mainAd.mediaType)}</span>
                </div>
              )}
              {mainAd.category && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Category:</span>
                  <span>{formatFieldValue(typeof mainAd.category === 'object' ? (mainAd.category?.name || mainAd.category?.title || '') : mainAd.category)}</span>
                </div>
              )}
              {mainAd.illumination && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Illumination:</span>
                  <span>{typeof mainAd.illumination === 'object' ? (mainAd.illumination?.name || mainAd.illumination?.title || '') : mainAd.illumination}</span>
                </div>
              )}
              {mainAd.size && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Size:</span>
                  <span>
                    {mainAd.size?.width} x {mainAd.size?.height}
                  </span>
                </div>
              )}
              {mainAd.class && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Class:</span>
                  <span>{mainAd.class}</span>
                </div>
              )}
              {mainAd.reach && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Reach:</span>
                  <span>{mainAd.reach}</span>
                </div>
              )}
              {mainAd.listeners && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Listeners:</span>
                  <span>{mainAd.listeners}</span>
                </div>
              )}
              {mainAd.total_readers  && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Readers:</span>
                  <span>{mainAd.total_readers}</span>
                </div>
              )}
              {mainAd.followers && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Followers:</span>
                  <span>{mainAd.followers}</span>
                </div>
              )}
              {mainAd.platforms && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Platforms:</span>
                  <span>{mainAd.platforms?.join(", ")}</span>
                </div>
              )}
              {mainAd.coverage && (
                <div className="flex items-start bg-gray-100 rounded-lg p-2">
                  <span className="font-semibold">Coverage:</span>
                  <span>{mainAd.coverage}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {childAds.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6">Related Options</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {childAds.map((ad) => (
                <Card
                  key={ad._id}
                  data={{
                    ...ad,
                    title: ad.name,
                  }}
                  category={category}
                  options={false}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <ClientSection />
      <ReachOutSection />
      <Footer />

      {/* YouTube Video Modal */}
      {showYoutubeModal && mainAd?.youtube_video_banner && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
          onClick={() => setShowYoutubeModal(false)}
        >
          <div
            className="relative w-full max-w-6xl aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute -top-12 right-0 text-white text-4xl font-bold hover:text-gray-300 transition-colors"
              onClick={() => setShowYoutubeModal(false)}
            >
              ×
            </button>
            <iframe
              className="w-full h-full rounded-lg"
              src={`https://www.youtube.com/embed/${getYoutubeVideoId(mainAd.youtube_video_banner)}?autoplay=1`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}

      <BrandEndorsementQuotationModal
        brandEndorsementName={mainAd?.title || mainAd?.name || mainAd?.publication_name}
        isOpen={showQuotationModal}
        onClose={() => setShowQuotationModal(false)}
        type={category}
      />
    </div>
    </>
  );
};

export default CategoryDetails;
